import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useBuscaProjetos } from '../../hooks/useBuscaProjetos';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isProjetosPage = location.pathname === '/nossos-projetos';
  const isHistoriaPage = location.pathname === '/historia';
  const { termoBusca, setTermoBusca, buscaAberta, setBuscaAberta } = useBuscaProjetos();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (buscaAberta && inputRef.current) {
      inputRef.current.focus();
    }
  }, [buscaAberta]);

  // Fecha busca ao sair da página
  useEffect(() => {
    if (!isProjetosPage) {
      setBuscaAberta(false);
      setTermoBusca('');
    }
  }, [isProjetosPage, setBuscaAberta, setTermoBusca]);

  const handleFecharBusca = () => {
    setBuscaAberta(false);
    setTermoBusca('');
  };

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    e.preventDefault();
    setMobileOpen(false);
    if (location.pathname === '/') {
      const element = document.querySelector(sectionId);
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    } else {
      sessionStorage.setItem('scrollToSection', sectionId);
      navigate('/');
    }
  };

  useEffect(() => {
    if (location.pathname === '/') {
      const sectionId = sessionStorage.getItem('scrollToSection');
      if (sectionId) {
        sessionStorage.removeItem('scrollToSection');
        const tryScroll = (attempts: number) => {
          const element = document.querySelector(sectionId);
          if (element) {
            setTimeout(() => element.scrollIntoView({ behavior: 'smooth' }), 100);
          } else if (attempts > 0) {
            setTimeout(() => tryScroll(attempts - 1), 150);
          }
        };
        tryScroll(20);
      }
    }
  }, [location.pathname]);

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={isScrolled ? { background: 'rgba(1,1,1,0.97)', backdropFilter: 'blur(12px)', boxShadow: '0 4px 30px rgba(0,0,0,0.8)', borderBottom: '1px solid #606060' } : {}}
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between relative">
        <a
          href="/#hero"
          onClick={(e) => handleNavClick(e, '#hero')}
          className="flex items-center gap-3 cursor-pointer"
        >
          <img
            src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/699b3a6347b6302630dd7c24/3edf42e3a_MGAlogob2.png"
            alt="MGA Blindagem"
            className="h-12 w-auto object-contain"
          />
          <div>
            <div className="font-black text-white text-lg leading-none tracking-wide">MGA</div>
            <div className="text-xs tracking-widest text-white font-bold uppercase">Blindagem</div>
          </div>
        </a>

        <div className="absolute left-1/2 -translate-x-1/2 hidden md:flex items-center gap-5">
          {[
            { label: 'Início', section: '#hero' },
            { label: 'Serviços', section: '#servicos' },
            { label: 'Quem Somos', section: '#quem-somos' },
          ].map((item) => (
            <a
              key={item.label}
              href={`/${item.section}`}
              onClick={(e) => handleNavClick(e, item.section)}
              className="text-sm font-medium tracking-widest uppercase cursor-pointer transition-colors whitespace-nowrap"
              style={{ color: '#a0a0a0' }}
              onMouseEnter={e => (e.currentTarget as HTMLElement).style.color = '#FB0000'}
              onMouseLeave={e => (e.currentTarget as HTMLElement).style.color = '#a0a0a0'}
            >
              {item.label}
            </a>
          ))}
          <a
            href="/historia"
            onClick={(e) => { e.preventDefault(); setMobileOpen(false); navigate('/historia'); }}
            className="text-sm font-medium tracking-widest uppercase cursor-pointer transition-colors whitespace-nowrap"
            style={{ color: isHistoriaPage ? '#FB0000' : '#a0a0a0' }}
            onMouseEnter={e => (e.currentTarget as HTMLElement).style.color = '#FB0000'}
            onMouseLeave={e => (e.currentTarget as HTMLElement).style.color = isHistoriaPage ? '#FB0000' : '#a0a0a0'}
          >
            Evolução
          </a>
          <a
            href="/nossos-projetos"
            onClick={(e) => { e.preventDefault(); setMobileOpen(false); navigate('/nossos-projetos'); }}
            className="text-sm font-medium tracking-widest uppercase cursor-pointer transition-colors whitespace-nowrap"
            style={{ color: isProjetosPage ? '#FB0000' : '#a0a0a0' }}
            onMouseEnter={e => (e.currentTarget as HTMLElement).style.color = '#FB0000'}
            onMouseLeave={e => (e.currentTarget as HTMLElement).style.color = isProjetosPage ? '#FB0000' : '#a0a0a0'}
          >
            Nossos Projetos
          </a>
          <a
            href="/#contato"
            onClick={(e) => handleNavClick(e, '#contato')}
            className="text-sm font-medium tracking-widest uppercase cursor-pointer transition-colors whitespace-nowrap"
            style={{ color: '#a0a0a0' }}
            onMouseEnter={e => (e.currentTarget as HTMLElement).style.color = '#FB0000'}
            onMouseLeave={e => (e.currentTarget as HTMLElement).style.color = '#a0a0a0'}
          >
            Contato
          </a>

        </div>

        <a
          href="https://api.whatsapp.com/send?phone=5511976350567"
          target="_blank"
          rel="noopener noreferrer"
          className="btn-gold px-5 py-2 rounded text-sm uppercase tracking-widest whitespace-nowrap cursor-pointer hidden md:block"
        >
          Fale Conosco
        </a>

        <button
          className="md:hidden text-white cursor-pointer"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden px-6 py-6 flex flex-col gap-4" style={{ background: 'rgba(1,1,1,0.98)', backdropFilter: 'blur(12px)', borderTop: '1px solid #606060' }}>
          {[
            { label: 'Início', section: '#hero' },
            { label: 'Serviços', section: '#servicos' },
            { label: 'Quem Somos', section: '#quem-somos' },
          ].map((item) => (
            <a
              key={item.label}
              href={`/${item.section}`}
              onClick={(e) => handleNavClick(e, item.section)}
              className="text-sm font-medium tracking-widest uppercase cursor-pointer transition-colors"
              style={{ color: '#a0a0a0' }}
            >
              {item.label}
            </a>
          ))}
          <a
            href="/historia"
            onClick={(e) => { e.preventDefault(); setMobileOpen(false); navigate('/historia'); }}
            className="text-sm font-medium tracking-widest uppercase cursor-pointer transition-colors"
            style={{ color: '#a0a0a0' }}
          >
            Evolução
          </a>
          <a
            href="/nossos-projetos"
            onClick={(e) => { e.preventDefault(); setMobileOpen(false); navigate('/nossos-projetos'); }}
            className="text-sm font-medium tracking-widest uppercase cursor-pointer transition-colors"
            style={{ color: '#a0a0a0' }}
          >
            Nossos Projetos
          </a>
          <a
            href="/#contato"
            onClick={(e) => handleNavClick(e, '#contato')}
            className="text-sm font-medium tracking-widest uppercase cursor-pointer transition-colors"
            style={{ color: '#a0a0a0' }}
          >
            Contato
          </a>
          <a
            href="https://api.whatsapp.com/send?phone=5511976350567"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-gold px-5 py-2 rounded text-sm uppercase tracking-widest whitespace-nowrap cursor-pointer text-center"
          >
            Fale Conosco
          </a>
        </div>
      )}
    </nav>
  );
}
